import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import {
  Scissors, Clock, DollarSign, CheckCircle, XCircle, AlertCircle,
  RefreshCw, ChevronDown, ChevronUp, Menu, User, CalendarDays, Pencil, Timer, Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import AdminSidebar from "@/components/AdminSidebar";
import { useNotificationCount } from "@/hooks/useNotificationCount";

const API = "/api";

const CutRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [adminNotes, setAdminNotes] = useState({});
  const [processing, setProcessing] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const notificationCount = useNotificationCount();
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ preferred_date: "", preferred_time: "", estimated_price: "" });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => { fetchRequests(); }, []);

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${API}/cut-requests`, { withCredentials: true });
      setRequests(res.data || []);
    } catch { toast.error("Failed to load cut requests"); }
    finally { setLoading(false); }
  };

  const handleUpdateStatus = async (requestId, status) => {
    setProcessing(requestId);
    try {
      await axios.put(`${API}/cut-requests/${requestId}`, {
        status,
        admin_notes: adminNotes[requestId] || ""
      }, { withCredentials: true });
      toast.success(`Request ${status === "approved" ? "approved" : "declined"}`);
      setExpandedId(null);
      fetchRequests();
    } catch { toast.error("Failed to update request"); }
    finally { setProcessing(null); }
  };

  const startEditing = (req) => {
    setEditingId(req.request_id);
    setEditData({
      preferred_date: req.preferred_date || "",
      preferred_time: req.preferred_time || "morning",
      estimated_price: req.estimated_price ? String(req.estimated_price) : "",
    });
  };

  const handleSaveEdit = async (requestId) => {
    setProcessing(requestId);
    try {
      await axios.put(`${API}/cut-requests/${requestId}`, {
        action: "edit",
        preferred_date: editData.preferred_date || undefined,
        preferred_time: editData.preferred_time || undefined,
        estimated_price: editData.estimated_price || undefined,
        admin_notes: adminNotes[requestId] || "",
      }, { withCredentials: true });
      toast.success("Edit sent — customer has 24 hours to accept");
      setEditingId(null);
      fetchRequests();
    } catch { toast.error("Failed to save edit"); }
    finally { setProcessing(null); }
  };

  const handleDelete = async (requestId) => {
    setProcessing(requestId);
    try {
      await axios.delete(`${API}/cut-requests/${requestId}`, { withCredentials: true });
      toast.success("Request deleted");
      setConfirmDeleteId(null);
      setExpandedId(null);
      fetchRequests();
    } catch { toast.error("Failed to delete"); }
    finally { setProcessing(null); }
  };

  const filtered = filter === "all" ? requests : requests.filter(r => r.status === filter);
  const counts = {
    all: requests.length,
    pending: requests.filter(r => r.status === "pending").length,
    admin_edited: requests.filter(r => r.status === "admin_edited").length,
    approved: requests.filter(r => r.status === "approved").length,
    declined: requests.filter(r => r.status === "declined").length,
  };

  const statusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      admin_edited: "bg-orange-500/20 text-orange-400 border-orange-500/30",
      approved: "bg-[#39FF14]/20 text-[#39FF14] border-[#39FF14]/30",
      declined: "bg-red-500/20 text-red-400 border-red-500/30",
    };
    const labels = { pending: "Pending", admin_edited: "Awaiting Customer", approved: "Approved", declined: "Declined" };
    return (
      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status] || ""}`} data-testid={`status-${status}`}>
        {labels[status] || status}
      </span>
    );
  };

  const formatTime = (t) => {
    const map = { morning: "Morning (8am-12pm)", afternoon: "Afternoon (12pm-4pm)", evening: "Evening (4pm-7pm)", anytime: "Anytime" };
    return map[t] || t;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      <AdminSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} notificationCount={notificationCount} />
      <div className="flex-1 flex flex-col min-h-screen">
        <header className="sticky top-0 z-30 bg-[#0A0A0A]/95 backdrop-blur border-b border-[#27272A] px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="lg:hidden text-white" onClick={() => setSidebarOpen(true)}>
                <Menu className="w-5 h-5" />
              </Button>
              <h1 className="font-heading text-2xl lg:text-3xl font-bold text-white uppercase">Cut Requests</h1>
              {counts.pending > 0 && (
                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">{counts.pending} pending</Badge>
              )}
            </div>
            <Button variant="outline" size="sm" className="border-[#27272A] text-[#A1A1AA]" onClick={() => { setLoading(true); fetchRequests(); }}>
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8">
          {/* Filter Tabs */}
          <div className="flex gap-2 mb-6 flex-wrap" data-testid="filter-tabs">
            {[
              { key: "all", label: "All" },
              { key: "pending", label: "Pending" },
              { key: "admin_edited", label: "Awaiting Customer" },
              { key: "approved", label: "Approved" },
              { key: "declined", label: "Declined" },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  filter === f.key
                    ? "bg-[#39FF14] text-black"
                    : "bg-[#121212] text-[#A1A1AA] border border-[#27272A] hover:border-[#39FF14]/30"
                }`}
                data-testid={`filter-${f.key}`}
              >
                {f.label} ({counts[f.key]})
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-[#39FF14] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <Scissors className="w-12 h-12 text-[#27272A] mx-auto mb-3" />
              <p className="text-[#A1A1AA]">No {filter !== "all" ? filter : ""} cut requests</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map(req => {
                const isExpanded = expandedId === req.request_id;
                return (
                  <div
                    key={req.request_id}
                    className={`bg-[#121212] border rounded-xl overflow-hidden transition-all ${
                      req.status === "pending" ? "border-yellow-500/30" : "border-[#27272A]"
                    }`}
                    data-testid={`cut-request-${req.request_id}`}
                  >
                    {/* Request Row */}
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#1a1a1a] transition-colors"
                      onClick={() => setExpandedId(isExpanded ? null : req.request_id)}
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                          req.status === "pending" ? "bg-yellow-500/20" : req.status === "approved" ? "bg-[#064E3B]" : "bg-red-500/10"
                        }`}>
                          <User className={`w-5 h-5 ${
                            req.status === "pending" ? "text-yellow-400" : req.status === "approved" ? "text-[#39FF14]" : "text-red-400"
                          }`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-white font-semibold truncate">{req.customer_name}</p>
                            {statusBadge(req.status)}
                            {req.returning_customer && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">Returning</span>
                            )}
                          </div>
                          <p className="text-[#A1A1AA] text-sm truncate">{req.customer_email}</p>
                          <p data-testid={`cut-request-address-${req.request_id}`} className={`text-sm truncate ${req.customer_address ? "text-[#39FF14]" : "text-red-400 italic"}`}>
                            {req.customer_address || "No address"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <div className="text-right hidden sm:block">
                          <div className="flex items-center gap-1.5 text-white text-sm">
                            <CalendarDays className="w-3.5 h-3.5 text-[#39FF14]" />
                            {req.preferred_date ? format(parseISO(req.preferred_date), "MMM d, yyyy") : "No date"}
                          </div>
                          <div className="flex items-center gap-1.5 text-[#A1A1AA] text-xs mt-0.5">
                            <Clock className="w-3 h-3" />
                            {formatTime(req.preferred_time)}
                          </div>
                        </div>
                        {req.estimated_price && !req.returning_customer ? (
                          <p className="text-[#39FF14] font-bold text-lg">${parseFloat(req.estimated_price).toFixed(2)}</p>
                        ) : req.returning_customer ? (
                          <p className="text-blue-400 text-sm font-medium">Existing price</p>
                        ) : null}
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-[#A1A1AA]" /> : <ChevronDown className="w-5 h-5 text-[#A1A1AA]" />}
                      </div>
                    </div>

                    {/* Expanded Detail */}
                    {isExpanded && (
                      <div className="border-t border-[#27272A] p-4 bg-[#0A0A0A] space-y-4">
                        {/* Awaiting customer banner */}
                        {req.status === "admin_edited" && (
                          <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg flex items-start gap-2">
                            <Timer className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
                            <div>
                              <p className="text-orange-400 text-sm font-medium">Waiting for customer approval</p>
                              <p className="text-[#A1A1AA] text-xs">
                                Expires {req.edit_expires_at ? format(parseISO(req.edit_expires_at), "MMM d 'at' h:mm a") : "in 24 hours"}
                              </p>
                              {req.proposed_changes && (
                                <div className="mt-2 space-y-1">
                                  <p className="text-[#A1A1AA] text-xs font-medium">Proposed changes:</p>
                                  {req.proposed_changes.preferred_date && (
                                    <p className="text-white text-xs">Date: <span className="line-through text-[#52525B]">{req.original_values?.preferred_date}</span> → <span className="text-orange-400">{req.proposed_changes.preferred_date}</span></p>
                                  )}
                                  {req.proposed_changes.preferred_time && (
                                    <p className="text-white text-xs">Time: <span className="line-through text-[#52525B]">{req.original_values?.preferred_time}</span> → <span className="text-orange-400">{formatTime(req.proposed_changes.preferred_time)}</span></p>
                                  )}
                                  {req.proposed_changes.estimated_price != null && (
                                    <p className="text-white text-xs">Price: <span className="line-through text-[#52525B]">${parseFloat(req.original_values?.estimated_price || 0).toFixed(2)}</span> → <span className="text-orange-400">${parseFloat(req.proposed_changes.estimated_price).toFixed(2)}</span></p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Edit Mode */}
                        {editingId === req.request_id ? (
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div className="space-y-1.5">
                                <Label className="text-[#A1A1AA] text-xs">Preferred Date</Label>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button variant="outline" className={`w-full justify-start text-left font-normal bg-[#121212] border-[#27272A] hover:bg-[#1a1a1a] ${editData.preferred_date ? "text-white" : "text-[#71717A]"}`} data-testid="edit-date-btn">
                                      <CalendarDays className="mr-2 h-4 w-4 text-[#39FF14]" />
                                      {editData.preferred_date ? format(new Date(editData.preferred_date + "T00:00:00"), "MMMM d, yyyy") : "Pick a date"}
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-auto p-0 bg-[#121212] border-[#39FF14]/30" align="start">
                                    <Calendar
                                      mode="single"
                                      selected={editData.preferred_date ? new Date(editData.preferred_date + "T00:00:00") : undefined}
                                      onSelect={(date) => {
                                        if (date) {
                                          const yyyy = date.getFullYear();
                                          const mm = String(date.getMonth() + 1).padStart(2, "0");
                                          const dd = String(date.getDate()).padStart(2, "0");
                                          setEditData({ ...editData, preferred_date: `${yyyy}-${mm}-${dd}` });
                                        }
                                      }}
                                      defaultMonth={new Date()}
                                      className="text-white"
                                      classNames={{
                                        day_selected: "bg-[#39FF14] text-black hover:bg-[#32D711] focus:bg-[#32D711]",
                                        day_today: "bg-[#39FF14]/20 text-[#39FF14]",
                                        caption_label: "text-white text-sm font-medium",
                                        nav_button: "text-[#39FF14] hover:bg-[#39FF14]/20 h-7 w-7 bg-transparent p-0",
                                        head_cell: "text-[#A1A1AA] rounded-md w-8 font-normal text-[0.8rem]",
                                        day: "h-8 w-8 p-0 font-normal text-white hover:bg-[#39FF14]/20 rounded-md",
                                        day_outside: "text-[#52525B] opacity-50",
                                      }}
                                    />
                                  </PopoverContent>
                                </Popover>
                              </div>
                              <div className="space-y-1.5">
                                <Label className="text-[#A1A1AA] text-xs">Preferred Time</Label>
                                <Select value={editData.preferred_time} onValueChange={(v) => setEditData({ ...editData, preferred_time: v })}>
                                  <SelectTrigger className="bg-[#121212] border-[#27272A] text-white" data-testid="edit-time-select">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-[#121212] border-[#27272A]">
                                    <SelectItem value="morning" className="text-white">Morning (8am-12pm)</SelectItem>
                                    <SelectItem value="afternoon" className="text-white">Afternoon (12pm-4pm)</SelectItem>
                                    <SelectItem value="evening" className="text-white">Evening (4pm-7pm)</SelectItem>
                                    <SelectItem value="anytime" className="text-white">Anytime</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-[#A1A1AA] text-xs">Price ($)</Label>
                              <div className="relative">
                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#39FF14]" />
                                <Input
                                  type="number"
                                  value={editData.estimated_price}
                                  onChange={(e) => setEditData({ ...editData, estimated_price: e.target.value })}
                                  className="pl-9 bg-[#121212] border-[#27272A] text-white"
                                  placeholder="e.g. 75.00"
                                  data-testid="edit-price-input"
                                />
                              </div>
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-[#A1A1AA] text-xs">Admin Notes</Label>
                              <Textarea
                                placeholder="Reason for changes..."
                                value={adminNotes[req.request_id] || ""}
                                onChange={(e) => setAdminNotes({ ...adminNotes, [req.request_id]: e.target.value })}
                                className="bg-[#121212] border-[#27272A] text-white h-16"
                              />
                            </div>
                            <div className="flex gap-3">
                              <Button
                                onClick={() => handleSaveEdit(req.request_id)}
                                disabled={processing === req.request_id}
                                className="flex-1 bg-orange-500 text-white hover:bg-orange-600 font-bold"
                                data-testid={`save-edit-${req.request_id}`}
                              >
                                <Timer className="w-4 h-4 mr-2" />
                                {processing === req.request_id ? "Saving..." : "Send Edit (24h to accept)"}
                              </Button>
                              <Button variant="outline" className="border-[#27272A] text-[#A1A1AA]" onClick={() => setEditingId(null)}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {/* Read-only detail */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-[#A1A1AA] text-xs mb-1">Preferred Date</p>
                                <p className="text-white">{req.preferred_date ? format(parseISO(req.preferred_date), "EEEE, MMMM d, yyyy") : "Not set"}</p>
                              </div>
                              <div>
                                <p className="text-[#A1A1AA] text-xs mb-1">Preferred Time</p>
                                <p className="text-white">{formatTime(req.preferred_time)}</p>
                              </div>
                              <div>
                                <p className="text-[#A1A1AA] text-xs mb-1">Estimated Price</p>
                                <p className="text-white">{req.returning_customer ? "Returning customer — use existing quote" : req.estimated_price ? `$${parseFloat(req.estimated_price).toFixed(2)}` : "Not provided"}</p>
                              </div>
                              <div>
                                <p className="text-[#A1A1AA] text-xs mb-1">Submitted</p>
                                <p className="text-white">{req.created_at ? format(parseISO(req.created_at), "MMM d, yyyy 'at' h:mm a") : "Unknown"}</p>
                              </div>
                            </div>
                            {req.notes && (
                              <div>
                                <p className="text-[#A1A1AA] text-xs mb-1">Customer Notes</p>
                                <p className="text-white text-sm bg-[#121212] rounded-lg p-3 border border-[#27272A]">{req.notes}</p>
                              </div>
                            )}

                            {req.status === "pending" && (
                              <div className="space-y-3 pt-2">
                                <div>
                                  <p className="text-[#A1A1AA] text-xs mb-1">Admin Notes (optional)</p>
                                  <Textarea
                                    placeholder="Add a note for this request..."
                                    value={adminNotes[req.request_id] || ""}
                                    onChange={(e) => setAdminNotes({ ...adminNotes, [req.request_id]: e.target.value })}
                                    className="bg-[#121212] border-[#27272A] text-white h-16"
                                    data-testid={`admin-notes-${req.request_id}`}
                                  />
                                </div>
                                <div className="flex gap-3">
                                  <Button
                                    onClick={() => handleUpdateStatus(req.request_id, "approved")}
                                    disabled={processing === req.request_id}
                                    className="flex-1 bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                                    data-testid={`approve-${req.request_id}`}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    {processing === req.request_id ? "Processing..." : "Approve"}
                                  </Button>
                                  <Button
                                    onClick={() => startEditing(req)}
                                    variant="outline"
                                    className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                                    data-testid={`edit-${req.request_id}`}
                                  >
                                    <Pencil className="w-4 h-4 mr-2" />
                                    Edit
                                  </Button>
                                  <Button
                                    onClick={() => handleUpdateStatus(req.request_id, "declined")}
                                    disabled={processing === req.request_id}
                                    variant="outline"
                                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                                    data-testid={`decline-${req.request_id}`}
                                  >
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Decline
                                  </Button>
                                  {confirmDeleteId === req.request_id ? (
                                    <Button
                                      onClick={() => handleDelete(req.request_id)}
                                      disabled={processing === req.request_id}
                                      className="bg-red-600 text-white hover:bg-red-700"
                                      data-testid={`confirm-delete-${req.request_id}`}
                                    >
                                      <Trash2 className="w-4 h-4 mr-1" />
                                      Confirm
                                    </Button>
                                  ) : (
                                    <Button
                                      onClick={() => setConfirmDeleteId(req.request_id)}
                                      variant="ghost"
                                      className="text-[#52525B] hover:text-red-400 hover:bg-red-500/10"
                                      data-testid={`delete-${req.request_id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            )}

                            {req.status !== "pending" && req.status !== "admin_edited" && req.admin_notes && (
                              <div>
                                <p className="text-[#A1A1AA] text-xs mb-1">Admin Response</p>
                                <p className="text-white text-sm bg-[#121212] rounded-lg p-3 border border-[#27272A]">{req.admin_notes}</p>
                              </div>
                            )}

                            {req.status !== "pending" && (
                              <div className="flex justify-end pt-2">
                                {confirmDeleteId === req.request_id ? (
                                  <Button
                                    onClick={() => handleDelete(req.request_id)}
                                    disabled={processing === req.request_id}
                                    size="sm"
                                    className="bg-red-600 text-white hover:bg-red-700"
                                    data-testid={`confirm-delete-${req.request_id}`}
                                  >
                                    <Trash2 className="w-4 h-4 mr-1" /> Confirm Delete
                                  </Button>
                                ) : (
                                  <Button
                                    onClick={() => setConfirmDeleteId(req.request_id)}
                                    variant="ghost"
                                    size="sm"
                                    className="text-[#52525B] hover:text-red-400 hover:bg-red-500/10"
                                    data-testid={`delete-${req.request_id}`}
                                  >
                                    <Trash2 className="w-4 h-4 mr-1" /> Delete
                                  </Button>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default CutRequests;
